import {
  logs,
  SeverityNumber,
  type AnyValue,
  type AnyValueMap,
} from '@opentelemetry/api-logs';
import { INSTRUMENTATION } from 'constant/instrumentation';
import {
  getNormalizedPathname,
  getWindowLocationPathname,
} from 'utils/functions/window';
import { LOCAL_STORAGE_KEYS } from 'constant/localStorage';
import { SESSION_STORAGE_KEYS } from 'constant/sessionStorage';

const logger = logs.getLogger(INSTRUMENTATION.SERVICE_NAME);

/** Read live user/tenant identity at emit time so logs are accurate even when
 *  the OTel resource was frozen before login. These attributes override the
 *  stale resource-level values in SigNoz's indexing. */
function getLiveUserAttrs(): AnyValueMap {
  return {
    screenName: getNormalizedPathname(), // /projects/:id  — for SigNoz grouping
    screenPath: getWindowLocationPathname(), // /projects/123 — raw ID for debugging
    'user.id':
      localStorage.getItem(LOCAL_STORAGE_KEYS.USER_ID) ?? 'UnknownUser',
    'user.email':
      sessionStorage.getItem(SESSION_STORAGE_KEYS.EMAIL) ?? 'UnknownEmail',
    'user.name':
      sessionStorage.getItem(SESSION_STORAGE_KEYS.USER_NAME) ?? 'UnknownUser',
    'tenant.id':
      localStorage.getItem(LOCAL_STORAGE_KEYS.TENANT) ?? 'UnknownTenant',
    'tenant.name':
      sessionStorage.getItem(SESSION_STORAGE_KEYS.TENANT_NAME) ??
      'UnknownTenant',
  };
}

/**
 * @param body - Use body for the main message
 * @param attrs - attributes for structured data
 */
export function emitInfoLog({
  body,
  attrs,
  severityNumber = SeverityNumber.INFO,
}: {
  body: AnyValue;
  attrs?: AnyValueMap;
  severityNumber?: SeverityNumber;
}) {
  logger.emit({
    body: body,
    severityNumber,
    severityText: 'INFO',
    attributes: {
      ...attrs,
      ...getLiveUserAttrs(),
    },
  });
}

export function emitWarnLog({
  body,
  attrs,
  severityNumber = SeverityNumber.WARN,
}: {
  body: AnyValue;
  attrs?: AnyValueMap;
  severityNumber?: SeverityNumber;
}) {
  logger.emit({
    body,
    severityNumber,
    severityText: 'WARN',
    attributes: {
      ...attrs,
      ...getLiveUserAttrs(),
    },
  });
}

export function emitErrorLog({
  body,
  attrs,
  severityNumber = SeverityNumber.ERROR,
}: {
  body: AnyValue;
  attrs?: AnyValueMap;
  severityNumber?: SeverityNumber;
}) {
  logger.emit({
    body,
    severityNumber,
    severityText: 'ERROR',
    attributes: {
      ...attrs,
      ...getLiveUserAttrs(),
    },
  });
}

export function emitFatalLog({
  body,
  attrs,
  severityNumber = SeverityNumber.FATAL,
}: {
  body: AnyValue;
  attrs?: AnyValueMap;
  severityNumber?: SeverityNumber;
}) {
  logger.emit({
    body,
    severityNumber,
    severityText: 'FATAL',
    attributes: {
      ...attrs,
      ...getLiveUserAttrs(),
    },
  });
}
